/**
 * "Does this device want less movement?" — asked once, in one place.
 *
 * Three components animate something the app could equally do without: the stamp band's leave,
 * a wrap cover's lift, the sealed result's flight. Each of them owes the same courtesy, and each
 * of them was about to grow its own copy of this hook — which is how two of them end up
 * disagreeing about what "reduced" means.
 *
 * It defaults to OFF and stays off when the platform cannot answer. Every animation behind it
 * carries nothing the static rendering does not already say, so it is safe to keep and safe to
 * drop — which makes losing it to a failed query the wrong failure.
 *
 * THE WEB ASKS A DIFFERENT API, AND IT HAS TO BE ASKED HERE. react-native-web does not implement
 * `isReduceMotionEnabled`, so this used to report false in a browser and the note here said the CSS
 * `prefers-reduced-motion` query was the answer instead. That was true only of animations a
 * stylesheet can reach. The Scoreboard's reveal counts its totals up in JavaScript, a wrap's cover
 * lifts through `Animated`, and no media query has any purchase on either — so on the one platform
 * this app is most often read on, the setting did nothing. `matchMedia` is the browser's own form
 * of the same question and is asked first; native falls through to `AccessibilityInfo` below.
 *
 * Subscribed rather than read once, in both forms: it is a setting somebody turns on part-way
 * through using an app, usually because the app is making them queasy.
 */
import * as React from 'react';
import { AccessibilityInfo, Platform } from 'react-native';

const REDUCE_QUERY = '(prefers-reduced-motion: reduce)';

export function useReducedMotion(): boolean {
  const [reduced, setReduced] = React.useState(false);

  React.useEffect(() => {
    if (Platform.OS === 'web') {
      const mql = globalThis.matchMedia?.(REDUCE_QUERY);
      if (!mql) return;
      setReduced(mql.matches);
      const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
      // `addEventListener` on a MediaQueryList is the modern spelling and the only one this app
      // needs — every browser that can run the client has had it for years — but it is still
      // guarded, because a `matchMedia` shim that answers `matches` and nothing else would
      // otherwise take the whole hook down with it.
      mql.addEventListener?.('change', onChange);
      return () => mql.removeEventListener?.('change', onChange);
    }

    let live = true;
    try {
      void AccessibilityInfo.isReduceMotionEnabled()
        .then((on) => {
          if (live) setReduced(!!on);
        })
        .catch(() => undefined);
    } catch {
      /* no accessibility API here */
    }

    const sub = AccessibilityInfo.addEventListener('reduceMotionChanged', (on) => setReduced(!!on));
    return () => {
      live = false;
      sub.remove();
    };
  }, []);

  return reduced;
}
